import { Box, Button, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import GroupInvitationNotification from "./GroupInvitationNotification";
import { useTranslation } from "react-i18next";
import useThemeStore from "../services/store/useThemeStore";

interface NotificationData {
  data?: {
    channel_name?: string;
    username?: string;
    time?: string;
    invited_by?: string;
    price: string;
    payment_status: string;
    id: number;
    card_id: string | number;
    booking_id?: number;
    talent_username?: string;
    suit?: string;
  };
  notification?: {
    title?: string;
    body?: string;
  };
}

interface FCMNotificationsProps {
  notification: NotificationData;
  notificationType:
  | "coffee"
  | "regularSession"
  | "groupCallInvitation"
  | "groupSession"
  | "missedGroupSession"
  | "groupInvitationConfirmation";
  closePopover: () => void;
}

const getWrapperStyles = (darkMode: boolean) => ({
  p: 1,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "10px",
  textAlign: "center",
  justifyContent: "space-between",
  border: darkMode ? "1px solid var(--neutral-600)" : "1px solid #E5E7EB",
  borderRadius: "4px",
  backgroundColor: darkMode ? "var(--neutral-900)" : "#fff",
  color: darkMode ? "var(--neutralWhite)" : "#111827",
});

const getButtonStyles = (darkMode: boolean) => ({
  background: "var(--button-gradient)",
  color: "var(--neutralWhite)",
  textTransform: "none",
  font: "var(--semibold-xxs)",
  padding: "var(--spacing-4x)",
  boxShadow: "none",
  minWidth: "85px",
  borderRadius: "8px !important",
  "&:hover": {
    background: "var(--button-gradient-hover)",
  },
});

const getButtonLabel = (
  type: FCMNotificationsProps["notificationType"],
  t: any,
) => {
  switch (type) {
    case "coffee":
      return t("join_call");
    case "missedGroupSession":
    case "groupSession":
      return t("join_session");
    case "regularSession":
      return t("join_session");
    default:
      return t("view");
  }
};

const FCMNotifications = ({
  notification,
  notificationType,
  closePopover,
}: FCMNotificationsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { darkMode } = useThemeStore();
  const {
    channel_name,
    username,
    talent_username,
    time,
    invited_by,
    payment_status,
    price,
    id,
    suit,
    card_id,
    booking_id,
  } = notification?.data || {};

  const CALLROUTES = {
    coffee: (channel: string) => `/coffee-call/${channel}`,
    regularSession: (channel: string) =>
      suit === "Heart"
        ? `/heart-live-stream/${channel}?bookingId=${booking_id}&cardId=${card_id}`
        : `/user/session/${channel}?bookingId=${booking_id}&cardId=${card_id}`,
    groupSession: (channel: string) =>
      suit === "Heart"
        ? `/heart-live-stream/${channel}?bookingId=${booking_id}&cardId=${card_id}`
        : `/user/group-session/${channel}?bookingId=${booking_id}&cardId=${card_id}`,
    missedGroupSession: (channel: string) => `/user/session/${channel}`,
    groupInvitationConfirmation: "",
    groupCallInvitation: "",
  };


  const cardName = (card_id: string | number) => {
    return "";
  };

  const handleClick = () => {
    const route = CALLROUTES[notificationType];

    if (typeof route === "function") {
      if (channel_name) {
        navigate(route(channel_name));
      } else {
        console.warn(
          "Missing channel_name for notificationType:",
          notificationType,
        );
      }
    } else if (typeof route === "string") {
      navigate(route);
      closePopover();
    } else {
      console.warn("Invalid notificationType:", notificationType);
    }
  };

  const renderSessionNotification = (
    title: string,
    description: string,
    username?: string,
  ) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        textAlign: "left",
      }}
    >
      <Typography
        sx={{
          font: "var(--semibold-xs)",
          color: darkMode ? "var(--neutralWhite)" : "var(--neutralBlack)",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          font: "var(--body-xxs)",
          lineHeight: "12px",
          color: darkMode ? "var(--neutral-300)" : "var(--neutral-700)",
        }}
      >
        {description}
      </Typography>
      <Typography
        sx={{
          font: "var(--semibold-xxs)",
          color: darkMode ? "var(--primary-400)" : "var(--primary-800)",
        }}
      >
        {t("talent")}: <Box component="span">{talent_username}</Box>
      </Typography>
      {card_id && (
        <Typography
          sx={{
            font: "var(--semibold-xxs)",
            color: darkMode ? "var(--primary-400)" : "var(--primary-800)",
          }}
        >
          {t("card_name")}: <Box component="span">{cardName(card_id)}</Box>
        </Typography>
      )}
    </Box>
  );

  const renderContent = () => {
    switch (notificationType) {
      case "coffee":
        return (
          <Typography
            sx={{
              font: "var(--semibold-xs)",
              color: darkMode ? "var(--neutralWhite)" : "var(--neutralBlack)",
            }}
          >
            {t("coffee_call_in_progress", { ns: "notification" })}
          </Typography>
        );

      case "regularSession":
        return renderSessionNotification(
          t("regular_session_in_progress", { ns: "notification" }),
          t("the_regular_session_that_you_scheduled_has_started", {
            ns: "notification",
          }),
          username,
        );

      case "groupCallInvitation":
        return (
          <GroupInvitationNotification
            closePopover={closePopover}
            username={username}
            time={time}
            cardName={card_id && cardName(card_id)}
            invited_by={invited_by}
            payment_status={payment_status}
            type="groupCallInvitation"
            price={price}
            suit={suit}
            talent_username={talent_username}
            onConfirm={() =>
              enqueueSnackbar(
                t("payment_confirmed_and_seat_booked", { ns: "notification" }),
                {
                  variant: "success",
                },
              )
            }
            onReject={() =>
              enqueueSnackbar(
                t("you_have_rejected_the_invitation", { ns: "notification" }),
                {
                  variant: "warning",
                },
              )
            }
          />
        );

      case "groupSession":
        return renderSessionNotification(
          t("group_session_in_progress", { ns: "notification" }),
          t("the_group_session_that_you_scheduled_has_started", {
            ns: "notification",
          }),
          username,
        );

      case "missedGroupSession":
        return renderSessionNotification(
          t("group_session_reminder", { ns: "notification" }),
          t("the_group_session_that_you_scheduled_has_already_been_started", {
            ns: "notification",
          }),
          username,
        );

      // case "groupInvitationConfirmation":
      //   return (
      //     <GroupInvitationNotification
      //       username={username}
      //       time={time}
      //       type="groupInvitationConfirmation"
      //       onConfirm={() =>
      //         enqueueSnackbar("Your seat has been booked.", {
      //           variant: "success",
      //         })
      //       }
      //       onReject={() =>
      //         enqueueSnackbar("You have rejected the invitation.", {
      //           variant: "warning",
      //         })
      //       }
      //     />
      //   );

      default:
        return null;
    }
  };

  return (
    <Box sx={getWrapperStyles(darkMode)}>
      {renderContent()}
      {notificationType !== "groupCallInvitation" && (
        <Button
          onClick={handleClick}
          variant="contained"
          sx={getButtonStyles(darkMode)}
        >
          {getButtonLabel(notificationType, t)}
        </Button>
      )}
    </Box>
  );
};

export default FCMNotifications;
